import {useId} from 'react'
import {RelativeTime} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import {
  CheckCircleFillIcon,
  CheckCircleIcon,
  ClockIcon,
  DotFillIcon,
  RocketIcon,
  SkipIcon,
  XCircleFillIcon,
} from '@primer/octicons-react'
import {Link} from '@github-ui/react-core/link'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {SidebarDeploymentsProps, DeploymentState, DeployToPagesInfo} from '../types'
import {SidebarSection} from './SidebarSection'
import {SidebarListItem} from './SidebarListItem'
import styles from './SidebarDeployments.module.css'

export function DeploymentStatusIcon({state}: {state: DeploymentState}) {
  switch (state) {
    case 'active':
      return <CheckCircleFillIcon className={styles.statusSuccess} />
    case 'error':
    case 'failure':
      return <XCircleFillIcon className={styles.statusDanger} />
    case 'inactive':
      return <CheckCircleIcon className={styles.statusMuted} />
    case 'in_progress':
      return (
        <svg
          aria-label="Deploy in progress"
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 16 16"
          className={styles.spinnerIcon}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke="var(--fgColor-attention)"
            strokeWidth="2"
            d="M3.05 3.05a7 7 0 1 1 9.9 9.9 7 7 0 0 1-9.9-9.9Z"
            opacity=".5"
          />
          <path
            fill="var(--fgColor-attention)"
            fillRule="evenodd"
            d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
            clipRule="evenodd"
          />
          <path fill="var(--fgColor-attention)" d="M14 8a6 6 0 0 0-6-6V0a8 8 0 0 1 8 8h-2Z" />
        </svg>
      )
    case 'waiting':
      return <ClockIcon className={styles.statusWarning} />
    case 'abandoned':
    case 'destroyed':
      return <SkipIcon className={styles.statusMuted} />
    case 'queued':
    case 'pending':
      return <DotFillIcon className={styles.statusWarning} />
    default:
      return <CheckCircleIcon className={styles.statusMuted} />
  }
}

function getDeploymentStatus(env: {state: string; createdAt?: string}) {
  if (env.state === 'active' && env.createdAt) {
    return <RelativeTime date={new Date(env.createdAt)} tense="past" />
  }
  if (env.state === 'inactive') return <span>inactive</span>
  if (env.state === 'queued' || env.state === 'pending') return <span>queued</span>
  return null
}

/** Renders the Deployments section of the repository sidebar. */
export function SidebarDeployments({
  environments,
  deploymentsPath,
  deployToPages,
  totalDeploymentCount,
  maxToShow: maxToShowProp = 3,
}: SidebarDeploymentsProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const totalEnvironments = environments.length
  const maxToShow =
    totalEnvironments === maxToShowProp + 1 ? totalEnvironments : Math.min(totalEnvironments, maxToShowProp)
  const displayed = environments.slice(0, maxToShow)

  const moreCount = totalEnvironments - maxToShow
  const displayCount =
    totalDeploymentCount != null ? (totalDeploymentCount > 500 ? '500+' : totalDeploymentCount) : totalEnvironments

  return (
    <SidebarSection
      title="Deployments"
      href={deploymentsPath}
      count={displayCount}
      moreCount={moreCount > 1 ? moreCount : undefined}
      moreLabel={`deployment${moreCount === 1 ? '' : 's'}`}
    >
      {totalEnvironments === 0 && deployToPages ? (
        <DeployToPagesCard {...deployToPages} />
      ) : totalEnvironments === 0 ? (
        <div className={styles.emptyState}>
          <span>No deployments published</span>
        </div>
      ) : (
        <ul className={styles.envList}>
          {displayed.map(env => (
            <SidebarListItem
              key={`deploy-${env.name}`}
              className={styles.envItem}
              icon={<DeploymentStatusIcon state={env.state} />}
              label={env.name}
              description={getDeploymentStatus(env)}
              href={env.path}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                sendClickAnalyticsEvent({
                  category: 'REPOSITORY_OVERVIEW',
                  action: 'click',
                  label: 'location:sidebar;section:deployments',
                })
              }
            />
          ))}
        </ul>
      )}
    </SidebarSection>
  )
}

function DeployToPagesCard({frameworkLabel, deployPath}: DeployToPagesInfo) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const id = useId()
  return (
    <div className={styles.deployCard}>
      <div className={styles.deployCardHeader}>
        <span className={styles.deployCardIcon} aria-hidden="true">
          <RocketIcon size={16} />
        </span>
        <span className={styles.deployCardTitle}>Deploy to Pages</span>
        <Link
          to={deployPath}
          reloadDocument
          aria-describedby={id}
          className={styles.deployCardButton}
          onClick={() =>
            sendClickAnalyticsEvent({
              category: 'Deploy to Pages',
              action: 'click_to_configure',
              label: 'ref_cta:configure;ref_loc:repo_sidebar',
            })
          }
        >
          Set up
        </Link>
      </div>
      <p className={styles.deployCardDescription} id={id}>
        Deploy your {frameworkLabel} to GitHub Pages in a matter of minutes
      </p>
    </div>
  )
}

export function SidebarDeploymentsSkeleton({
  environmentNames,
  maxToShow: maxToShowProp = 3,
}: {
  environmentNames?: string[]
  maxToShow?: number
}) {
  const maxToShow =
    environmentNames?.length === maxToShowProp + 1
      ? environmentNames.length
      : Math.min(environmentNames?.length ?? 0, maxToShowProp)
  const displayed = environmentNames?.slice(0, maxToShow) ?? []

  if (displayed.length > 0) {
    return (
      <SidebarSection title="Deployments">
        <ul className={styles.envList}>
          {displayed.map(name => (
            <li key={`deploy-skeleton-${name}`} className={styles.envItem}>
              <SkeletonText size="bodySmall" />
            </li>
          ))}
        </ul>
      </SidebarSection>
    )
  }

  return (
    <SidebarSection title="Deployments">
      <SkeletonText />
    </SidebarSection>
  )
}
